// Инструмент «Линейка» на карте (кнопка с иконкой Ruler в MapView.vue).
//
// Клик по карте ставит вершину, следующий клик - вторую и т.д. Вершины -
// перетаскиваемые MapLibre-маркеры. Правый клик (десктоп) или долгий тап
// (мобильный) по линии открывает меню «добавить точку здесь», по вершине -
// «удалить точку».
//
// Расстояния каждого сегмента и всей ломаной считаются на клиенте (haversine,
// мгновенно). Набор/потеря высоты берутся с бэкенда
// (POST /terrain/elevation-profile) - тот же DEM и тот же анкерный алгоритм,
// что и у маршрутов; вне покрытия DEM (не район Алматы) линейка показывает
// только длину.
import * as maplibregl from "maplibre-gl";
import type { Marker } from "maplibre-gl";
import type { Feature, FeatureCollection, LineString } from "geojson";
import { computed, ref } from "vue";
import api from "../api";
import { DEFAULT_ROUTE_STYLE, haversineDistanceM } from "../utils/routeLineStyle";
import { setGeoJsonSourceData } from "../map/mapUiHelpers";
import { RULER_LINE_SOURCE } from "../map/constants";
import type { MapCtx } from "../map/mapContext";
import { useRoutingPath, type Coordinate } from "./useRoutingPath";
import { trackEvent } from "../analytics";

const LINE_LAYER = "ruler-line-layer";
const CASING_LAYER = "ruler-line-casing-layer";
// --primary дизайн-системы литералом: paint-свойства MapLibre не читают CSS-переменные.
const RULER_COLOR = "#1f7a5c";
const RULER_LINE_WIDTH = 3;
const RULER_CASING_WIDTH = RULER_LINE_WIDTH + DEFAULT_ROUTE_STYLE.casing.extraWidth;
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE_PX = 12;

export interface RulerSegmentStats {
  distanceM: number;
  elevationGainM: number | null;
  elevationLossM: number | null;
}

export interface RulerTotals {
  distanceM: number;
  elevationGainM: number | null;
  elevationLossM: number | null;
  hasDemCoverage: boolean;
  // высота есть, но не по всей линии - итог по измеримым сегментам
  partialCoverage: boolean;
}

export interface RulerLeg {
  geometry: Coordinate[];
  routed: boolean;
  destinationMode: "network" | "poi";
  status: "routed" | "straight" | "fallback";
}

export type RulerMenuKind = "vertex" | "line";

export interface RulerMenuState {
  kind: RulerMenuKind;
  x: number;
  y: number;
  // vertex: индекс вершины; line: индекс, ПЕРЕД которым вставить новую точку
  index: number;
  // line: координаты, куда встанет новая точка
  lngLat?: [number, number];
  // line: routed-статус отрезка под курсором на момент открытия меню -
  // решает, какой из двух пунктов ("спрямить"/"примагнитить") показать.
  legRouted?: boolean;
}

interface ElevationProfileResponse {
  total_distance_m: number;
  total_elevation_gain_m: number | null;
  total_elevation_loss_m: number | null;
  has_dem_coverage: boolean;
  partial_coverage: boolean;
  segments: {
    distance_m: number;
    elevation_gain_m: number | null;
    elevation_loss_m: number | null;
  }[];
  vertex_elevations: (number | null)[];
}

export function useMapRuler(ctx: MapCtx) {
  const active = ref(false);
  const vertices = ref<[number, number][]>([]); // [lng, lat]
  const totals = ref<RulerTotals | null>(null);
  const segments = ref<RulerSegmentStats[]>([]);
  const menu = ref<RulerMenuState | null>(null);
  const legs = ref<RulerLeg[]>([]);
  const magnetEnabled = ref(false);
  const routing = useRoutingPath();
  const routeError = ref(false);
  // Карточка ошибки: true - проложить нельзя (на пути река/обрыв, обхода нет),
  // а не просто «проведена прямая» (routingFallback).
  const routeErrorBlocked = ref(false);
  // Карточка предупреждения "выезжает" из-под панели линейки на
  // ROUTE_ERROR_DISPLAY_MS, потом сама уезжает обратно вниз - таймер живёт
  // здесь, а не в MapView.vue, чтобы каждый вызов setRouteError(true)
  // (в т.ч. подряд, на соседних отрезках) надёжно продлевал показ, а не
  // полагался на изменение значения ref для срабатывания watch'а.
  const ROUTE_ERROR_DISPLAY_MS = 4000;
  let routeErrorTimer: number | null = null;
  const setRouteError = (failed: boolean, blocked = false) => {
    if (routeErrorTimer != null) {
      window.clearTimeout(routeErrorTimer);
      routeErrorTimer = null;
    }
    routeError.value = failed;
    routeErrorBlocked.value = failed && blocked;
    if (failed) {
      routeErrorTimer = window.setTimeout(() => {
        routeError.value = false;
        routeErrorTimer = null;
      }, ROUTE_ERROR_DISPLAY_MS);
    }
  };

  // ── История для undo/redo (Ctrl+Z/Ctrl+Shift+Z / кнопки) ─────────────
  // Снимок перед каждым мутирующим действием (клик-добавление, удаление,
  // вставка через меню, перетаскивание, очистка) - undo просто откатывает
  // vertices/legs на предыдущий снимок, redo - обратно. Новое мутирующее
  // действие обнуляет future (как везде: сделал новый шаг после отмены -
  // прежний "будущий" ход уже не восстановить). Ограничение размера -
  // страховка от неограниченного роста в долгой сессии, не расчёт на
  // реальное достижение.
  const MAX_HISTORY = 50;
  type RulerSnapshot = { vertices: Coordinate[]; legs: RulerLeg[] };
  const history = ref<RulerSnapshot[]>([]);
  const future = ref<RulerSnapshot[]>([]);
  const canUndo = computed(() => history.value.length > 0);
  const canRedo = computed(() => future.value.length > 0);

  const cloneVertices = (): Coordinate[] => vertices.value.map((v) => [v[0], v[1]] as Coordinate);
  const cloneLegs = (): RulerLeg[] =>
    legs.value.map((leg) => ({ ...leg, geometry: leg.geometry.map((c) => [c[0], c[1]] as Coordinate) }));
  const snapshot = (): RulerSnapshot => ({ vertices: cloneVertices(), legs: cloneLegs() });

  const pushHistory = () => {
    history.value.push(snapshot());
    if (history.value.length > MAX_HISTORY) history.value.shift();
    future.value = [];
  };

  // Откатить только что записанный шаг, не отдавая его в «повторить»: действие
  // не состоялось (проложить нельзя), а не было отменено пользователем.
  const revertLastStep = () => {
    const prev = history.value.pop();
    if (!prev) return;
    vertices.value = prev.vertices;
    legs.value = prev.legs;
    closeMenu();
    redraw();
    scheduleElevation();
  };

  const undo = () => {
    if (routing.pending.value) return;
    const prev = history.value.pop();
    if (!prev) return;
    future.value.push(snapshot());
    if (future.value.length > MAX_HISTORY) future.value.shift();
    vertices.value = prev.vertices;
    legs.value = prev.legs;
    closeMenu();
    redraw();
    scheduleElevation();
  };

  const redo = () => {
    if (routing.pending.value) return;
    const next = future.value.pop();
    if (!next) return;
    history.value.push(snapshot());
    if (history.value.length > MAX_HISTORY) history.value.shift();
    vertices.value = next.vertices;
    legs.value = next.legs;
    closeMenu();
    redraw();
    scheduleElevation();
  };

  const markers: Marker[] = [];
  let layersReady = false;
  let elevationRequestId = 0;
  let elevationTimer: number | null = null;
  let draggingVertex = false;
  // Подавляет map "click" сразу после перетаскивания вершины/закрытия меню -
  // иначе dragend/тап тут же поставил бы лишнюю вершину.
  let suppressNextClick = false;

  const getMap = () => ctx.getMap();

  const renderedCoordinates = (): Coordinate[] => {
    if (!legs.value.length) return vertices.value.slice();
    const out: Coordinate[] = [];
    for (const leg of legs.value) {
      for (const coordinate of leg.geometry) {
        const last = out.at(-1);
        if (!last || coordinate[0] !== last[0] || coordinate[1] !== last[1]) out.push(coordinate);
      }
    }
    return out;
  };

  const legDistanceM = (leg: RulerLeg): number => {
    let total = 0;
    for (let i = 1; i < leg.geometry.length; i++) {
      total += haversineDistanceM(leg.geometry[i - 1][1], leg.geometry[i - 1][0], leg.geometry[i][1], leg.geometry[i][0]);
    }
    return total;
  };

  // ── Геометрия ──────────────────────────────────────────────────────────

  const segmentDistancesM = (): number[] => legs.value.map(legDistanceM);

  const midpoint = (a: [number, number], b: [number, number]): [number, number] => [
    (a[0] + b[0]) / 2,
    (a[1] + b[1]) / 2,
  ];

  // Ближайшая точка на ломаной к произвольной координате: возвращает индекс
  // ОТРЕЗКА (leg, не пары соседних вершин) и саму проекцию. Плоская проекция
  // в градусах - на масштабе линейки (десятки км) погрешность незаметна,
  // зато без зависимостей.
  //
  // Идёт по geometry КАЖДОГО leg'а (может быть несколько точек, если он
  // примагничен/проложен по сети), а не по прямой между его двумя
  // вершинами - иначе у только что примагниченного отрезка, чья реальная
  // (кривая, следующая тропе) линия на карте могла заметно уйти в сторону
  // от прямой между его концами, долгий тап по видимой линии не находил
  // ничего в пределах допуска, и меню молча не открывалось ("перестаёт
  // работать" - фидбог с прода).
  const nearestOnPolyline = (
    lng: number,
    lat: number
  ): { segmentIndex: number; point: [number, number]; distDeg: number } | null => {
    if (vertices.value.length < 2) return null;
    let best: { segmentIndex: number; point: [number, number]; distDeg: number } | null = null;
    for (let legIndex = 0; legIndex < legs.value.length; legIndex++) {
      const geometry = legs.value[legIndex].geometry;
      for (let i = 1; i < geometry.length; i++) {
        const [ax, ay] = geometry[i - 1];
        const [bx, by] = geometry[i];
        const dx = bx - ax;
        const dy = by - ay;
        const lenSq = dx * dx + dy * dy || 1e-12;
        let t = ((lng - ax) * dx + (lat - ay) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
        const px = ax + t * dx;
        const py = ay + t * dy;
        const d = Math.hypot(lng - px, lat - py);
        if (!best || d < best.distDeg) {
          best = { segmentIndex: legIndex, point: [px, py], distDeg: d };
        }
      }
    }
    return best;
  };

  // ── Отрисовка ─────────────────────────────────────────────────────────

  const buildLineFeature = (): Feature<LineString> => ({
    type: "Feature",
    properties: {},
    geometry: { type: "LineString", coordinates: renderedCoordinates() },
  });

  const redraw = () => {
    const map = getMap();
    if (!map || !layersReady) return;
    const line = renderedCoordinates().length >= 2 ? [buildLineFeature()] : [];
    setGeoJsonSourceData(map, RULER_LINE_SOURCE, line);
    syncMarkers();
    recomputeTotalsLocal();
  };

  // Пересчитывает быстрые (клиентские) итоги сразу; набор/потерю дотянет
  // scheduleElevation после ответа сервера.
  const recomputeTotalsLocal = () => {
    const dists = segmentDistancesM();
    const distanceM = dists.reduce((a, b) => a + b, 0);
    if (vertices.value.length < 2) {
      totals.value = null;
      return;
    }
    const prev = totals.value;
    totals.value = {
      distanceM,
      elevationGainM: prev?.elevationGainM ?? null,
      elevationLossM: prev?.elevationLossM ?? null,
      hasDemCoverage: prev?.hasDemCoverage ?? false,
      partialCoverage: prev?.partialCoverage ?? false,
    };
  };

  const previewAdjacentLegs = (vertexIndex: number) => {
    for (const index of [vertexIndex - 1, vertexIndex]) {
      const leg = legs.value[index];
      if (leg) leg.geometry = [vertices.value[index], vertices.value[index + 1]];
    }
  };

  // ── Маркеры вершин ────────────────────────────────────────────────────

  const makeVertexElement = (index: number): HTMLElement => {
    const el = document.createElement("div");
    el.className = "ruler-vertex";
    el.textContent = String(index + 1);
    el.setAttribute("role", "button");
    el.setAttribute("aria-label", ctx.t("map.ruler.vertexAria"));

    el.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      openMenuAt(e.clientX, e.clientY, { kind: "vertex", index });
    });

    // Долгий тап по вершине на мобильном обрабатывается НЕ здесь, а общим
    // onTouchStart на контейнере карты (ниже) - у него теперь приоритетная
    // проверка близости к вершине (nearestVertexIndexAt, радиус шире
    // видимого пина). Раньше свой таймер был прямо на этом элементе без
    // stopPropagation - оба таймера (этот и общий, для линии) стартовали на
    // одном тапе, и какой срабатывал позже (тот же LONG_PRESS_MS), тот и
    // перезаписывал меню - долгий тап по точке иногда открывал меню линии.
    return el;
  };

  // Приводит набор маркеров к текущему vertices: MapLibre-маркеры дёшево
  // пересоздать целиком, чем сверять по индексам (их единицы-десятки).
  const beginVertexDrag = () => {
    pushHistory();
    draggingVertex = true;
    closeMenu();
  };

  // Отпустили точку: смежные отрезки перекладываются по сети. Проложить нельзя
  // (река/обрыв, обхода нет) - точка и отрезки возвращаются как были.
  const finishVertexDrag = async (index: number, ll: { lng: number; lat: number }) => {
    vertices.value[index] = [ll.lng, ll.lat];
    draggingVertex = true;
    suppressNextClick = true;
    window.setTimeout(() => {
      draggingVertex = false;
    }, 0);
    if (!(await rebuildAdjacentLegs(index))) revertLastStep();
  };

  const syncMarkers = () => {
    const map = getMap();
    if (!map) return;
    while (markers.length) markers.pop()!.remove();
    vertices.value.forEach((coord, index) => {
      const marker = new maplibregl.Marker({
        element: makeVertexElement(index),
        draggable: true,
      })
        .setLngLat(coord)
        .addTo(map);
      marker.on("dragstart", beginVertexDrag);
      marker.on("drag", () => {
        const ll = marker.getLngLat();
        vertices.value[index] = [ll.lng, ll.lat];
        previewAdjacentLegs(index);
        const m = getMap();
        if (m) {
          const line = vertices.value.length >= 2 ? [buildLineFeature()] : [];
          setGeoJsonSourceData(m, RULER_LINE_SOURCE, line);
        }
      });
      marker.on("dragend", () => void finishVertexDrag(index, marker.getLngLat()));
      markers.push(marker);
    });
  };

  // ── Меню (правый клик / долгий тап) ───────────────────────────────────

  const openMenuAt = (
    clientX: number,
    clientY: number,
    base: { kind: RulerMenuKind; index: number; lngLat?: [number, number]; legRouted?: boolean }
  ) => {
    const map = getMap();
    const rect = map?.getContainer().getBoundingClientRect();
    menu.value = {
      ...base,
      x: rect ? clientX - rect.left : clientX,
      y: rect ? clientY - rect.top : clientY,
    };
    // Синтетический click, который мобильный браузер может послать после
    // long-press, иначе тут же закрыл бы только что открытое меню.
    suppressNextClick = true;
  };

  const closeMenu = () => {
    menu.value = null;
  };

  // Удаляет вершину, трогая только смежные с ней отрезки - остальная
  // (уже, возможно, проложенная магнитом) геометрия линии не спрямляется.
  // Внутренняя вершина: два соседних отрезка схлопываются в один новый
  // между оставшимися соседями (по сети, если магнит включён - как будто
  // только что кликнули между ними). Крайняя вершина: просто пропадает
  // прилегающий к ней единственный отрезок, стыковать нечего.
  const deleteVertexAt = (idx: number) => {
    if (idx < 0 || idx >= vertices.value.length) return;
    pushHistory();
    const isFirst = idx === 0;
    const isLast = idx === vertices.value.length - 1;
    vertices.value.splice(idx, 1);
    if (!isFirst && !isLast) {
      legs.value.splice(idx - 1, 2, {
        geometry: [vertices.value[idx - 1], vertices.value[idx]],
        routed: magnetEnabled.value,
        destinationMode: "network",
        status: "straight",
      });
    } else if (isFirst) {
      legs.value.splice(0, 1);
    } else {
      legs.value.splice(legs.value.length - 1, 1);
    }
    redraw();
    if (!isFirst && !isLast && magnetEnabled.value) {
      void routeLeg(idx - 1).then((ok) => {
        if (!ok) {
          revertLastStep();
          return;
        }
        redraw();
        scheduleElevation();
      });
    } else {
      scheduleElevation();
    }
  };

  const deleteLastVertex = () => {
    if (routing.pending.value || !vertices.value.length) return;
    deleteVertexAt(vertices.value.length - 1);
  };

  const handleMenuSelect = (action: string) => {
    const current = menu.value;
    closeMenu();
    if (!current) return;
    if (action === "delete-vertex") {
      deleteVertexAt(current.index);
    } else if (action === "add-vertex" && current.lngLat) {
      // Тот же принцип: рвём только ОДИН отрезок, на который встаёт новая
      // точка, на два прямых - остальные отрезки (в т.ч. проложенные
      // магнитом) не трогаем.
      pushHistory();
      const insertAt = current.index;
      vertices.value.splice(insertAt, 0, current.lngLat);
      legs.value.splice(
        insertAt - 1,
        1,
        {
          geometry: [vertices.value[insertAt - 1], vertices.value[insertAt]],
          routed: false, destinationMode: "network", status: "straight",
        },
        {
          geometry: [vertices.value[insertAt], vertices.value[insertAt + 1]],
          routed: false, destinationMode: "network", status: "straight",
        }
      );
      redraw();
      scheduleElevation();
    } else if (action === "straighten-leg") {
      straightenLeg(current.index - 1);
    } else if (action === "magnetize-leg") {
      void magnetizeLeg(current.index - 1);
    }
    suppressNextClick = true;
    window.setTimeout(() => {
      suppressNextClick = false;
    }, 0);
  };

  const buildElevationRequest = (): { vertices: Coordinate[]; ranges: [number, number][] } => {
    if (!legs.value.length) return { vertices: [], ranges: [] };
    const maxVertices = 50;
    if (legs.value.length + 1 > maxVertices) {
      const full = renderedCoordinates();
      return {
        vertices: Array.from({ length: maxVertices }, (_, i) =>
          full[Math.round((i * (full.length - 1)) / (maxVertices - 1))]
        ),
        ranges: [],
      };
    }
    const interiorAvailable = maxVertices - legs.value.length - 1;
    const interiorTotal = legs.value.reduce((sum, leg) => sum + Math.max(0, leg.geometry.length - 2), 0);
    const vertices: Coordinate[] = [legs.value[0].geometry[0]];
    const ranges: [number, number][] = [];
    let remaining = interiorAvailable;
    for (let legIndex = 0; legIndex < legs.value.length; legIndex++) {
      const geometry = legs.value[legIndex].geometry;
      const interior = Math.max(0, geometry.length - 2);
      const extra = legIndex === legs.value.length - 1
        ? Math.min(interior, remaining)
        : Math.min(interior, Math.floor(interiorAvailable * interior / Math.max(1, interiorTotal)));
      const segmentStart = vertices.length - 1;
      for (let sample = 1; sample <= extra + 1; sample++) {
        vertices.push(geometry[Math.round(sample * (geometry.length - 1) / (extra + 1))]);
      }
      remaining -= extra;
      ranges.push([segmentStart, vertices.length - 1]);
    }
    return { vertices, ranges };
  };

  // ── Высотный профиль с бэкенда ───────────────────────────────────────

  const scheduleElevation = () => {
    if (elevationTimer != null) window.clearTimeout(elevationTimer);
    elevationTimer = window.setTimeout(fetchElevation, 350);
  };

  const fetchElevation = async () => {
    const elevationInput = buildElevationRequest();
    if (elevationInput.vertices.length < 2) {
      segments.value = [];
      totals.value = null;
      return;
    }
    const v = elevationInput.vertices;
    const requestId = ++elevationRequestId;
    try {
      const { data } = await api.post<ElevationProfileResponse>("/terrain/elevation-profile", {
        vertices: v,
      });
      if (requestId !== elevationRequestId) return; // устарел
      segments.value = legs.value.map((leg, index) => {
        const range = elevationInput.ranges[index];
        const parts = range ? data.segments.slice(range[0], range[1]) : [];
        const gains = parts.map((part) => part.elevation_gain_m);
        const losses = parts.map((part) => part.elevation_loss_m);
        return {
          distanceM: legDistanceM(leg),
          elevationGainM: parts.length && gains.every((value) => value != null)
            ? gains.reduce((sum, value) => sum + (value ?? 0), 0) : null,
          elevationLossM: parts.length && losses.every((value) => value != null)
            ? losses.reduce((sum, value) => sum + (value ?? 0), 0) : null,
        };
      });
      totals.value = {
        distanceM: segmentDistancesM().reduce((sum, value) => sum + value, 0),
        elevationGainM: data.total_elevation_gain_m,
        elevationLossM: data.total_elevation_loss_m,
        hasDemCoverage: data.has_dem_coverage,
        partialCoverage: data.partial_coverage,
      };
    } catch {
      if (requestId !== elevationRequestId) return;
      // Сеть/DEM отказали - оставляем клиентские расстояния, высоту гасим.
      segments.value = [];
      recomputeTotalsLocal();
    }
  };

  // ── Обработчики карты ────────────────────────────────────────────────

  // lngLat вместо целого MapMouseEvent - переиспользуется и живым кликом
  // (magneticTarget ниже, из e.lngLat), и постфактум применённым магнитом
  // (applyMagnetRetroactively - у уже поставленной точки живого события
  // с готовым e.point нет, только координата, проецируем сами).
  const magneticTargetAt = (lngLat: Coordinate): { mode: "network" | "poi"; destination: Coordinate } | null => {
    const map = getMap();
    if (!map) return null;
    const p = map.project(lngLat);
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
      [p.x - 24, p.y - 24], [p.x + 24, p.y + 24],
    ];
    const layers = ["pois-icon-unclustered", "pois-peak", "pois-pass", "pois-power-tower", "pois-peak-admin", "pois-pass-admin", "trails-line", "roads-line"]
      .filter((id) => Boolean(map.getLayer(id)));
    const feature = map.queryRenderedFeatures(box, { layers })[0];
    if (!feature) return null;
    if (feature.layer.id.startsWith("pois-") && feature.geometry.type === "Point") {
      return { mode: "poi", destination: feature.geometry.coordinates.slice(0, 2) as Coordinate };
    }
    return { mode: "network", destination: lngLat };
  };

  const magneticTarget = (e: maplibregl.MapMouseEvent) => magneticTargetAt([e.lngLat.lng, e.lngLat.lat]);

  // false - проложить нельзя (status "blocked": на пути река/обрыв, обхода нет):
  // отрезок не меняется, показана карточка, решение (откат) - за вызывающим.
  const routeLeg = async (index: number): Promise<boolean> => {
    const leg = legs.value[index];
    if (!leg?.routed) return true;
    const result = await routing.route(vertices.value[index], vertices.value[index + 1], {
      strategy: "shortest", destinationMode: leg.destinationMode, snapRadiusM: 100,
    });
    if (!result) return true;
    if (result.status === "blocked") {
      setRouteError(true, true);
      return false;
    }
    legs.value[index] = { ...leg, geometry: result.geometry, status: result.status === "routed" ? "routed" : "fallback" };
    setRouteError(result.status !== "routed");
    return true;
  };

  // Точечное примагничивание/спрямление ОДНОГО отрезка - из меню по долгому
  // тапу/правому клику на линии (handleMenuSelect: "magnetize-leg"/
  // "straighten-leg"). Раньше это делало включение магнита сразу для ВСЕХ
  // уже поставленных отрезков - пользователю не понравилось (откачено),
  // взамен управление по одному отрезку за раз.
  const magnetizeLeg = async (index: number) => {
    const leg = legs.value[index];
    if (!leg || leg.routed) return;
    pushHistory();
    const target = magneticTargetAt(vertices.value[index + 1]);
    const destinationMode = target?.mode ?? "network";
    legs.value[index] = { ...leg, routed: true, destinationMode };
    if (!(await routeLeg(index))) {
      revertLastStep();
      return;
    }
    // "network"-маршрут прокладывается до ближайшей сети в пределах
    // snap_radius_m и НЕ дотягивает геометрию обратно до присланной точки
    // (в отличие от "poi" - см. _route() в backend/app/routing/service.py,
    // geometry.append(payload.destination) только в ветке "poi"). Если саму
    // вершину не подтянуть к фактическому концу линии, точка остаётся на
    // исходном (не примагниченном) месте, а линия заканчивается чуть в
    // стороне - "отрывается" от точки (репорт с прода). При живом клике с
    // магнитом это уже учтено (onMapClick::endpoint) - переносим тот же приём.
    const routedLeg = legs.value[index];
    if (destinationMode === "network" && routedLeg.status === "routed") {
      const newEndpoint = routedLeg.geometry.at(-1)!;
      vertices.value[index + 1] = newEndpoint;
      const nextLeg = legs.value[index + 1];
      if (nextLeg && !nextLeg.routed) {
        legs.value[index + 1] = { ...nextLeg, geometry: [newEndpoint, vertices.value[index + 2]] };
      } else if (nextLeg?.routed && !(await routeLeg(index + 1))) {
        revertLastStep();
        return;
      }
    }
    redraw();
    scheduleElevation();
  };

  const straightenLeg = (index: number) => {
    const leg = legs.value[index];
    if (!leg || !leg.routed) return;
    pushHistory();
    legs.value[index] = {
      geometry: [vertices.value[index], vertices.value[index + 1]],
      routed: false,
      destinationMode: "network",
      status: "straight",
    };
    redraw();
    scheduleElevation();
  };

  const rebuildAdjacentLegs = async (vertexIndex: number): Promise<boolean> => {
    for (const index of [vertexIndex - 1, vertexIndex]) {
      if (index >= 0 && index < legs.value.length && !(await routeLeg(index))) return false;
    }
    redraw();
    scheduleElevation();
    return true;
  };

  // Новая точка линейки. target - цель магнита под тапом (или null: прямая).
  // Проложить нельзя (на пути река/обрыв, обхода нет) - точка не добавляется, шаг
  // истории не пишется, показывается карточка.
  const addVertex = async (
    destination: Coordinate,
    target: { mode: "network" | "poi"; destination: Coordinate } | null
  ): Promise<boolean> => {
    const previous = vertices.value.at(-1);
    if (!previous) {
      pushHistory();
      vertices.value.push(destination);
      redraw();
      return true;
    }
    let geometry: Coordinate[] = [previous, destination];
    let status: RulerLeg["status"] = "straight";
    if (target) {
      const result = await routing.route(previous, target.destination, {
        strategy: "shortest", destinationMode: target.mode, snapRadiusM: 100,
      });
      if (result?.status === "blocked") {
        setRouteError(true, true);
        return false;
      }
      if (result) {
        geometry = result.geometry;
        status = result.status === "routed" ? "routed" : "fallback";
        setRouteError(result.status !== "routed");
      } else {
        status = "fallback";
        setRouteError(true);
      }
    }
    pushHistory();
    const endpoint = target?.mode === "network" && status === "routed" ? geometry.at(-1)! : (target?.destination ?? destination);
    vertices.value.push(endpoint);
    legs.value.push({ geometry, routed: Boolean(target), destinationMode: target?.mode ?? "network", status });
    redraw();
    scheduleElevation();
    return true;
  };

  const onMapClick = async (e: maplibregl.MapMouseEvent) => {
    if (!active.value || routing.pending.value) return;
    if (suppressNextClick || draggingVertex) {
      suppressNextClick = false;
      return;
    }
    if (menu.value) {
      closeMenu();
      return;
    }
    setRouteError(false);
    const destination: Coordinate = [e.lngLat.lng, e.lngLat.lat];
    const target = magnetEnabled.value && vertices.value.length ? magneticTarget(e) : null;
    await addVertex(destination, target);
  };

  const onMapContextMenu = (e: maplibregl.MapMouseEvent) => {
    if (!active.value || vertices.value.length < 2) return;
    e.preventDefault();
    maybeOpenLineMenu(e.lngLat.lng, e.lngLat.lat, e.originalEvent.clientX, e.originalEvent.clientY);
  };

  // Открывает меню линии, только если клик достаточно близко к ней (иначе
  // правый клик по пустой карте открывал бы бесполезное меню).
  const maybeOpenLineMenu = (lng: number, lat: number, clientX: number, clientY: number) => {
    const map = getMap();
    if (!map) return;
    const near = nearestOnPolyline(lng, lat);
    if (!near) return;
    const projected = map.project(near.point as maplibregl.LngLatLike);
    const clicked = map.project([lng, lat]);
    if (Math.hypot(projected.x - clicked.x, projected.y - clicked.y) > 24) return;
    openMenuAt(clientX, clientY, {
      kind: "line",
      index: near.segmentIndex + 1,
      lngLat: near.point,
      legRouted: legs.value[near.segmentIndex]?.routed ?? false,
    });
  };

  // Долгий тап по карте - мобильный аналог правого клика, по вершине ИЛИ
  // по линии. Вершина - в приоритете: её зона захвата (VERTEX_HIT_RADIUS_PX)
  // шире видимого пина (.ruler-vertex, ~20px), иначе палец, легший чуть мимо
  // маленького кружка, открывал бы меню линии вместо точки (жалоба с прода).
  const VERTEX_HIT_RADIUS_PX = 26;
  const nearestVertexIndexAt = (clientX: number, clientY: number): number | null => {
    const map = getMap();
    const rect = map?.getContainer().getBoundingClientRect();
    if (!map || !rect) return null;
    let best: number | null = null;
    let bestDist = Infinity;
    vertices.value.forEach((v, i) => {
      const p = map.project(v as maplibregl.LngLatLike);
      const d = Math.hypot(p.x - (clientX - rect.left), p.y - (clientY - rect.top));
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best !== null && bestDist <= VERTEX_HIT_RADIUS_PX ? best : null;
  };

  let mapPressTimer: number | null = null;
  let mapPressStart: [number, number] | null = null;
  const onTouchStart = (e: TouchEvent) => {
    if (!active.value || !vertices.value.length || e.touches.length !== 1) return;
    const tch = e.touches[0];
    mapPressStart = [tch.clientX, tch.clientY];
    mapPressTimer = window.setTimeout(() => {
      if (!mapPressStart) return;
      const vertexIndex = nearestVertexIndexAt(mapPressStart[0], mapPressStart[1]);
      if (vertexIndex != null) {
        openMenuAt(mapPressStart[0], mapPressStart[1], { kind: "vertex", index: vertexIndex });
        return;
      }
      if (vertices.value.length < 2) return;
      const map = getMap();
      const rect = map?.getContainer().getBoundingClientRect();
      if (!map || !rect) return;
      const ll = map.unproject([mapPressStart[0] - rect.left, mapPressStart[1] - rect.top]);
      maybeOpenLineMenu(ll.lng, ll.lat, mapPressStart[0], mapPressStart[1]);
    }, LONG_PRESS_MS);
  };
  const clearMapPress = () => {
    if (mapPressTimer != null) window.clearTimeout(mapPressTimer);
    mapPressTimer = null;
    mapPressStart = null;
  };
  const onTouchMove = (e: TouchEvent) => {
    const tch = e.touches[0];
    if (!tch || !mapPressStart) return;
    if (
      Math.hypot(tch.clientX - mapPressStart[0], tch.clientY - mapPressStart[1]) >
      LONG_PRESS_MOVE_TOLERANCE_PX
    ) {
      clearMapPress();
    }
  };

  // ── Публичное API ────────────────────────────────────────────────────

  const setLayerVisibility = (visible: boolean) => {
    const map = getMap();
    if (!map || !layersReady) return;
    const v = visible ? "visible" : "none";
    for (const id of [CASING_LAYER, LINE_LAYER]) {
      if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", v);
    }
  };

  const setupLayers = () => {
    const map = getMap();
    if (!map || layersReady) return;

    map.addSource(RULER_LINE_SOURCE, { type: "geojson", data: emptyFc() });

    // Route-style casing keeps the ruler readable over contours and roads.
    map.addLayer({
      id: CASING_LAYER,
      type: "line",
      source: RULER_LINE_SOURCE,
      layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": DEFAULT_ROUTE_STYLE.casing.color,
        "line-width": RULER_CASING_WIDTH,
        "line-opacity": DEFAULT_ROUTE_STYLE.casing.opacity,
      },
    });
    map.addLayer({
      id: LINE_LAYER,
      type: "line",
      source: RULER_LINE_SOURCE,
      layout: { visibility: "none", "line-cap": "round", "line-join": "round" },
      paint: {
        "line-color": RULER_COLOR,
        "line-width": RULER_LINE_WIDTH,
        "line-dasharray": [1.6, 1.4],
      },
    });
    map.on("click", onMapClick);
    map.on("contextmenu", onMapContextMenu);
    const container = map.getContainer();
    container.addEventListener("touchstart", onTouchStart, { passive: true });
    container.addEventListener("touchmove", onTouchMove, { passive: true });
    container.addEventListener("touchend", clearMapPress, { passive: true });
    container.addEventListener("touchcancel", clearMapPress, { passive: true });

    layersReady = true;
    // Кнопку могли включить до готовности карты - тогда activate() не смог
    // показать слои; сейчас карта готова, синхронизируем видимость.
    if (active.value) {
      setLayerVisibility(true);
      map.getCanvas().style.cursor = "crosshair";
    }
  };

  // recordHistory=false для внутреннего вызова из deactivate() - закрытие
  // линейки не должно оставлять отменяемую запись, история целиком
  // сбрасывается там же следующей строкой.
  const clearVertices = (recordHistory = true) => {
    if (recordHistory && vertices.value.length) pushHistory();
    vertices.value = [];
    legs.value = [];
    segments.value = [];
    totals.value = null;
    elevationRequestId++;
    magnetEnabled.value = false;
    setRouteError(false);
    routing.reset();
    closeMenu();
    redraw();
  };

  const activate = () => {
    active.value = true;
    trackEvent("Ruler: Activate");
    setupLayers();
    setLayerVisibility(true);
    const map = getMap();
    if (map) map.getCanvas().style.cursor = "crosshair";
  };

  const deactivate = () => {
    active.value = false;
    closeMenu();
    setLayerVisibility(false);
    clearVertices(false);
    history.value = [];
    future.value = [];
    const map = getMap();
    if (map) map.getCanvas().style.cursor = "";
  };

  const toggle = () => {
    if (active.value) deactivate();
    else activate();
  };

  const destroy = () => {
    if (elevationTimer != null) window.clearTimeout(elevationTimer);
    if (routeErrorTimer != null) window.clearTimeout(routeErrorTimer);
    clearMapPress();
    while (markers.length) markers.pop()!.remove();
    const map = getMap();
    if (map) {
      map.off("click", onMapClick);
      map.off("contextmenu", onMapContextMenu);
      const container = map.getContainer();
      container.removeEventListener("touchstart", onTouchStart);
      container.removeEventListener("touchmove", onTouchMove);
      container.removeEventListener("touchend", clearMapPress);
      container.removeEventListener("touchcancel", clearMapPress);
    }
  };

  return {
    active,
    vertices,
    totals,
    segments,
    menu,
    legs,
    magnetEnabled,
    routingPending: routing.pending,
    routingStatus: routing.lastStatus,
    routeError,
    routeErrorBlocked,
    addVertex,
    beginVertexDrag,
    finishVertexDrag,
    magnetizeLeg,
    canUndo,
    undo,
    canRedo,
    redo,
    deleteLastVertex,
    rebuildAdjacentLegs,
    toggle,
    activate,
    deactivate,
    clearVertices,
    handleMenuSelect,
    closeMenu,
    setupLayers,
    destroy,
  };
}

// ── Форматирование ──────────────────────────────────────────────────────

export function formatDistance(meters: number): string {
  if (meters < 1000) return `${Math.round(meters)} м`;
  return `${(meters / 1000).toFixed(meters < 10000 ? 2 : 1)} км`;
}

function emptyFc(): FeatureCollection {
  return { type: "FeatureCollection", features: [] };
}
